import { createAction, createSlice } from '@reduxjs/toolkit'
import { LOGOUT_ACTION_TYPES } from '@/features/auth/logoutActionTypes'

/** A page-mount load reuses what the store holds when it was loaded this recently. */
export const FRESH_FOR_MS = 60_000

const loadRecorded = createAction('freshness/loadRecorded')

/**
 * Forget every recorded load. For paths that create or change entities
 * outside the list thunks' reducers (the auto-setup and bulk measurement
 * runs, the onboarding starter dashboard), so the next page visit reads the
 * server instead of reusing a list from before the change.
 */
export const loadsInvalidated = createAction('freshness/loadsInvalidated')

// The assistant's confirmed drafts create metrics, dashboards and widgets
// server side.
const FORGET_ALL_TYPES = new Set([...LOGOUT_ACTION_TYPES, loadsInvalidated.type, 'aiChat/confirmDraft/fulfilled'])
const argKey = (arg) => JSON.stringify(arg ?? null)

/**
 * `{ [thunk typePrefix]: { argKey, at } }`: the last load `loadIfStale` saw
 * succeed for each thunk. Any later success of the same thunk with other
 * arguments (a filtered list replacing the full one) drops the record; a
 * logout, a confirmed assistant draft, `loadsInvalidated` and - since this
 * slice is not preserved - a tenant switch drop them all.
 */
const freshnessSlice = createSlice({
  name: 'freshness',
  initialState: {},
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadRecorded, (state, action) => {
        const { typePrefix, key, at } = action.payload
        state[typePrefix] = { argKey: key, at }
      })
      .addMatcher((action) => FORGET_ALL_TYPES.has(action.type), () => ({}))
      .addMatcher(
        (action) => action.meta?.requestStatus === 'fulfilled' && typeof action.type === 'string',
        (state, action) => {
          const typePrefix = action.type.slice(0, -'/fulfilled'.length)
          const record = state[typePrefix]
          if (record && record.argKey !== argKey(action.meta.arg)) delete state[typePrefix]
        },
      )
  },
})

export default freshnessSlice.reducer

/**
 * Dispatch `thunk(arg)` unless the same load succeeded less than
 * `FRESH_FOR_MS` ago and its data is still in the store. For page-mount
 * loads only: a refresh after a mutation dispatches the thunk directly.
 *
 * @param {import('@reduxjs/toolkit').AsyncThunk} thunk
 * @param {*} [arg]
 * @param {{ isPresent?: (state: object) => boolean }} [options] - for a
 *   thunk whose data can be cleared outside this record (the current dashboard)
 * @returns {import('@reduxjs/toolkit').ThunkAction<Promise<object|null>>}
 *   the thunk's settled action, or `null` when the stored data was reused
 */
export function loadIfStale(thunk, arg, { isPresent } = {}) {
  return async (dispatch, getState) => {
    const key = argKey(arg)
    const state = getState()
    const record = state.freshness?.[thunk.typePrefix]
    const isFresh = record?.argKey === key && Date.now() - record.at < FRESH_FOR_MS
    if (isFresh && (!isPresent || isPresent(state))) return null
    const result = await dispatch(thunk(arg))
    // `?.`: page tests stand plain action creators in for the thunks.
    if (thunk.fulfilled?.match(result)) {
      dispatch(loadRecorded({ typePrefix: thunk.typePrefix, key, at: Date.now() }))
    }
    return result
  }
}
